import {
    type BackgroundColourStyle,
    type BorderColourStyle,
    type InputStyleSheet,
    type Stylable,
    type StyleKey,
    type StyleSheetKey,
    type StyleSheetRecord,
    type StyleValue,
    type TextColourStyle,
    Theme,
    type ThemeColourMap,
    type ThemeColours,
    type ThemeStyles,
} from "@qendo/theme";
import { Platform, StyleSheet } from "react-native";
import { MD3DarkTheme, MD3LightTheme, type MD3Theme } from "react-native-paper";
import { base, darkBlue, darkPink, lightBlue, lightPink } from "~/constants/colours";
import { INPUT_HEIGHT, Radius, Space } from "~/constants/sizing";
import { FontHeights, LineHeights } from "~/constants/typography";

export function makeStyleRecords<
    Type extends Stylable = Stylable,
    Style extends StyleKey<Type> = StyleKey<Type>,
    Value extends StyleValue<Type, Style> = StyleValue<Type, Style>,
    Key extends string = string,
    Variant extends string = "",
>(
    from: Record<Key, Value>,
    fields: StyleKey<Type> | StyleKey<Type>[],
    key?: Variant,
): StyleSheetRecord<StyleSheetKey<Key, Variant>, Type, Style, Value> {
    return Object.fromEntries(
        (Object.entries(from) as [Key, Value][]).map<[StyleSheetKey<Key, Variant>, Record<Style, Value>]>(
            ([name, val]) => [
                (key?.length ? `${key}_${name}` : name) as StyleSheetKey<Key, Variant>,
                Array.isArray(fields)
                    ? (Object.fromEntries(
                          (fields as Style[]).map<[Style, Value]>((k) => [k as Style, val as Value]),
                      ) as Record<Style, Value>)
                    : ({ [fields as Style]: val as Value } as Record<Style, Value>),
            ],
        ),
    ) as StyleSheetRecord<StyleSheetKey<Key, Variant>, Type, Style, Value>;
}

export const ThemeDarkMap: Record<Theme, boolean> = {
    [Theme.light_blue]: false,
    [Theme.dark_blue]: true,
    [Theme.light_pink]: false,
    [Theme.dark_pink]: true,
};

export const ThemeMap: ThemeColourMap = {
    [Theme.light_blue]: lightBlue,
    [Theme.dark_blue]: darkBlue,
    [Theme.light_pink]: lightPink,
    [Theme.dark_pink]: darkPink,
};

export function makeStyleSheets(theme: ThemeColours): ThemeStyles {
    return {
        text: StyleSheet.create<TextColourStyle>(makeStyleRecords(theme.text, "color")),
        background: StyleSheet.create<BackgroundColourStyle>(makeStyleRecords(theme.background, "backgroundColor")),
        border: StyleSheet.create<BorderColourStyle>({
            ...makeStyleRecords(theme.border, "borderColor"),
            ...makeStyleRecords(theme.border, "borderTopColor", "top"),
            ...makeStyleRecords(theme.border, "borderRightColor", "right"),
            ...makeStyleRecords(theme.border, "borderBottomColor", "bottom"),
            ...makeStyleRecords(theme.border, "borderLeftColor", "left"),
        }),
    };
}

export function makeInputStyles(theme: ThemeColours): InputStyleSheet {
    return StyleSheet.create<InputStyleSheet>({
        input: {
            paddingVertical: Space.sm,
            paddingHorizontal: Space.sm,
            borderRadius: Radius.md,
            borderColor: theme.border.primary,
            borderWidth: 2,
            height: INPUT_HEIGHT,
            outlineWidth: 0,
            backgroundColor: theme.background.primary,
        },
        inputText: {
            fontWeight: "normal",
            color: theme.text.primary,
            fontSize: FontHeights.base,
            lineHeight: Platform.select({ native: LineHeights.sm, default: LineHeights.base }),
        },
        textArea: {
            height: "auto",
            minHeight: INPUT_HEIGHT,
            justifyContent: "flex-start",
        },
        focus: {
            borderColor: theme.border.selected,
        },
        error: {
            borderColor: theme.border.danger,
        },
        disabled: {
            backgroundColor: theme.background.secondary,
            borderColor: theme.border.secondary,
        },
        disabledText: {
            color: theme.text.secondary,
        },
        placeholder: {
            fontSize: FontHeights.base,
            lineHeight: LineHeights.base,
            color: theme.text.tertiary,
            fontWeight: 400,
        },
        shadow: {
            boxShadow: [
                {
                    offsetX: 0,
                    offsetY: 1,
                    blurRadius: 4,
                    spreadDistance: 3,
                    color: theme.boxShadow,
                },
            ],
        },
    });
}

export function makePaperTheme(theme: Theme): MD3Theme {
    const colours = ThemeMap[theme];
    const base_theme = ThemeDarkMap[theme] ? MD3DarkTheme : MD3LightTheme;

    return {
        ...base_theme,
        dark: ThemeDarkMap[theme],
        roundness: 1, // paper multiplies roundness by 7
        colors: {
            ...base_theme.colors,
            primary: colours.text.primary,
            onPrimary: colours.text.selected,
            primaryContainer: colours.border.primary,
            onPrimaryContainer: colours.border.primary,
            secondary: colours.text.secondary,
            onSecondary: colours.text.selected,
            secondaryContainer: colours.border.secondary,
            onSecondaryContainer: colours.border.secondary,
            tertiary: colours.text.tertiary,
            onTertiary: colours.text.selected,
            tertiaryContainer: colours.border.tertiary,
            onTertiaryContainer: colours.border.tertiary,
            background: colours.background.primary,
            onBackground: colours.background["primary-pressed"],
            surface: colours.background.secondary,
            onSurface: colours.background["secondary-pressed"],
            elevation: {
                level0: base.transparent,
                level1: colours.background.primary,
                level2: colours.background.primary,
                level3: colours.background.primary,
                level4: colours.background.primary,
                level5: colours.background.primary,
            },
        },
    };
}
